// src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import registerVideo from '../assets/register.mp4';

function Login({ onLogin }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const response = await axios.post('https://madadline.com/api/login', {
        email,
        password,
      });

      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user_id', response.data.user.id);
      localStorage.setItem('user_data', JSON.stringify(response.data.user));

      if (onLogin) onLogin(response.data.user);

      if (response.data.user.email === 'admin@gmail.com') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (error) {
      setMessage(
        error.response?.data?.message === 'Identifiants invalides'
          ? t('auth.login.errorInvalid')
          : t('auth.login.errorGeneric')
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-creative">

      <div className="auth-creative__deco auth-creative__deco--1" />
      <div className="auth-creative__deco auth-creative__deco--2" />
      <div className="auth-creative__deco auth-creative__deco--3" />

      <div className="auth-creative__visual">
        <video className="auth-creative__video" autoPlay loop muted playsInline>
          <source src={registerVideo} type="video/mp4" />
        </video>
        <div className="auth-creative__visual-overlay" />

        <div className="auth-creative__visual-content">
          <span className="auth-creative__eyebrow">{t('auth.visualEyebrow')}</span>

          <h1 className="auth-creative__visual-title">
            {t('auth.login.visualTitleLine1')}<br />
            <em>{t('auth.login.visualTitleEm')}</em>
          </h1>

          <p className="auth-creative__visual-text">
            {t('auth.login.visualText')}
          </p>

          <div className="auth-creative__stats">
            <div className="auth-creative__stat">
              <span className="auth-creative__stat-value">5000+</span>
              <span className="auth-creative__stat-label">{t('auth.statsClients')}</span>
            </div>
            <div className="auth-creative__stat">
              <span className="auth-creative__stat-value">150+</span>
              <span className="auth-creative__stat-label">{t('auth.statsCities')}</span>
            </div>
            <div className="auth-creative__stat">
              <span className="auth-creative__stat-value">24/7</span>
              <span className="auth-creative__stat-label">{t('auth.statsSupport')}</span>
            </div>
          </div>

          <div className="auth-creative__quote">
            <span className="auth-creative__quote-mark">"</span>
            <p>{t('auth.login.quote')}</p>
          </div>
        </div>

        <div className="auth-creative__badge">
          <span className="auth-creative__badge-ar">مداد</span>
          <span className="auth-creative__badge-sep" />
          <span className="auth-creative__badge-fr">MADAD</span>
        </div>
      </div>

      <div className="auth-creative__form-side">
        <div className="auth-creative__form-container">

          <Link to="/" className="auth-creative__logo">
            <div className="auth-creative__logo-mark">
              <span className="auth-creative__logo-ar">مداد</span>
              <span className="auth-creative__logo-fr">MADAD</span>
            </div>
            <span className="auth-creative__logo-tag">{t('auth.logoTag')}</span>
          </Link>

          <div
            className="auth-creative__header"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.6s ease',
            }}
          >
            <h2>{t('auth.login.heading')}</h2>
            <p>{t('auth.login.subheading')}</p>
          </div>

          <form className="auth-creative__form" onSubmit={handleLogin}>
            <div
              className="auth-creative__field"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
                transition: 'all 0.5s ease 0.1s',
              }}
            >
              <label htmlFor="email">{t('auth.login.labelEmail')}</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.login.placeholderEmail')}
                autoComplete="email"
                required
              />
              <span className="auth-creative__field-line" />
            </div>

            <div
              className="auth-creative__field"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
                transition: 'all 0.5s ease 0.2s',
              }}
            >
              <div className="auth-creative__label-row">
                <label htmlFor="password">{t('auth.login.labelPassword')}</label>
                <Link to="/forget-password" className="auth-creative__forgot">
                  {t('auth.login.forgot')}
                </Link>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.login.placeholderPassword')}
                autoComplete="current-password"
                required
              />
              <span className="auth-creative__field-line" />
            </div>

            <button
              type="submit"
              className="auth-creative__submit"
              disabled={isLoading}
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
                transition: 'all 0.5s ease 0.3s',
              }}
            >
              {isLoading ? (
                <span className="auth-creative__spinner" />
              ) : (
                <>
                  {t('auth.login.submit')}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </>
              )}
            </button>

            {message && <div className="auth-creative__message">{message}</div>}
          </form>

          <div className="auth-creative__divider">
            <span /><em>{t('auth.divider')}</em><span />
          </div>

          <p className="auth-creative__switch">
            {t('auth.login.switchText')}{' '}
            <Link to="/register">{t('auth.login.switchLink')}</Link>
          </p>

        </div>
      </div>
    </div>
  );
}

export default Login;